import { default as Analytics, AnalyticsInstance } from 'analytics';
import fullStoryPlugin from '@analytics/fullstory';
import { adobeAnalyticsPlugin, datadogRumPlugin, getAnalyticsUserId, optimizelyPlugin } from '@odmonorepo/odfunctions';
import tmAnalytics from 'libs/odfunctions/src/lib/tmProfiling.js';
import { optimizelyInstance } from 'merchant-api/vendor/optimizely';

const IS_DEV = import.meta.env.NODE_ENV === 'development';
const serviceName = 'ondeck_merchant_ux';

const plugins = [
  fullStoryPlugin({
    org: import.meta.env.VITE_FULLSTORY_TOKEN,
  }),
  datadogRumPlugin({
    applicationId: import.meta.env.VITE_DATADOG_APP_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    serviceName: serviceName,
  }),
  adobeAnalyticsPlugin(),
  optimizelyPlugin(optimizelyInstance),
  tmAnalytics(),
];

const analyticsInstance: AnalyticsInstance = Analytics({
  app: serviceName,
  debug: IS_DEV,
  plugins: IS_DEV ? [] : plugins,
});

// Store the last identify data to compare for changes
let lastIdentifyData: string | null = null;

// Wrap the analytics instance to add change detection for identify events
const originalIdentify = analyticsInstance.identify.bind(analyticsInstance);

const tryGetAdobeMarketingCloudVisitorId = (): string | undefined => {
  try {
    const id = (window as any)?.s?.visitor?.getMarketingCloudVisitorID?.();
    return typeof id === 'string' && id.length > 0 ? id : undefined;
  } catch {
    return undefined;
  }
};

const enrichTraitsWithAdobeId = (traits?: any) => {
  const adobeMarketingCloudVisitorId = tryGetAdobeMarketingCloudVisitorId();
  if (!adobeMarketingCloudVisitorId) return traits;
  return { ...(traits ?? {}), adobeMarketingCloudVisitorId };
};

export const analytics: AnalyticsInstance & { getUserId: () => string } = {
  ...analyticsInstance,
  identify: ((userId: any, traits?: any, options?: any, callback?: any) => {
    const userIdToUse = userId || getAnalyticsUserId();
    const enrichedTraits = enrichTraitsWithAdobeId(traits);

    // Serialize the new identify data
    const newData = JSON.stringify({ userId: userIdToUse, traits: enrichedTraits });

    // Only call the original identify if data has changed
    if (newData !== lastIdentifyData) {
      lastIdentifyData = newData;
      return originalIdentify(userIdToUse, enrichedTraits, options, callback);
    }

    // Return a no-op promise if data hasn't changed
    return Promise.resolve();
  }) as any,
  getUserId: getAnalyticsUserId,
};

/**
 * Helper function to track field events.
 * @param formName - The name of the form.
 * @param fieldName - The name of the field.
 * @param eventType - The type of event (e.g., 'blur', 'focus', 'change').
 */
export const trackField = (formName: string | undefined, fieldName: string, eventType: string) => {
  analytics.track(`Field${eventType}`, {
    form: formName,
    field: fieldName,
  });
};

export default analytics;
